import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useGetExpensesQuery,
  useUpdateExpenseMutation,
  useUploadExpenseImagesMutation,
} from '../../slices/expensesApiSlice';

const BulkEditExpensesScreen = () => {
  const [updates, setUpdates] = useState([]);
  const [fileName, setFileName] = useState('');

  const { data } = useGetExpensesQuery({ keyword: '', pageNumber: 1 });
  const [updateExpense, { isLoading: loadingUpdate }] = useUpdateExpenseMutation();
  const [uploadExpenseImages, { isLoading: loadingUpload }] = useUploadExpenseImagesMutation();

  const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

  const validateRow = (row) => {
    if (!row.ExpenseId || !isValidObjectId(row.ExpenseId.trim())) return 'Invalid or missing Expense ID';
    if (!row.AccountName) return 'Missing Account Name';
    if (!['Debit', 'Credit'].includes(row.Account)) return 'Invalid Account';
    if (isNaN(parseFloat(row.Amount))) return 'Invalid Amount';
    
    if (!row.Date || isNaN(Date.parse(row.Date))) return 'Invalid Date';
    return null;
  };

  
  const handleFileUpload = (e) => {
  const file = e.target.files[0];
  if (!file || !data?.expenses) return;

  setFileName(file.name);
  const reader = new FileReader();
  reader.onload = (evt) => {
    const workbook = XLSX.read(evt.target.result, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    
    const enriched = rows.map((row, index) => {
      const expenseId = row.ExpenseId.trim();
      const match = data.expenses.find((exp) => exp._id === expenseId);
      const error = validateRow(row) || (!match ? 'No matching expense found' : null);

      return {
        index,
        expenseId,
        matchedExpense: match,
        date: formatDate(row.Date),
        account: row.Account,
        accountName: row.AccountName,
        subAccount: row.SubAccount,
        details: row.Details,
        amount: parseFloat(row.Amount),
        category: row.Category,
        notes: row.Notes,
        images: row.Images ? row.Images.split(',').map((url) => url.trim()) : [],
        error,
        status: null,
      };
    });

    setUpdates(enriched);
    toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
  };

  reader.readAsArrayBuffer(file);
};

  const formatDate = (value) => {
  const parsed = new Date(value);
  if (!isNaN(parsed)) return parsed.toISOString().split('T')[0];
  return ''; // fallback for invalid dates
};

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('images', file);

    try {
      const res = await uploadExpenseImages(formData).unwrap();
      const updated = [...updates];
      updated[index].images = res.images;
      setUpdates(updated);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleRowSubmit = async (row, index) => {
    if (row.error) {
      toast.warning(`Row ${index + 1} skipped: ${row.error}`);
      return;
    }

    try {
      await updateExpense({
        expenseId: row.expenseId,
        date: row.date,
        account: row.account,
        accountName: row.accountName,
        subAccount: row.subAccount,
        details: row.details,
        amount: row.amount,
        category: row.category,
        notes: row.notes,
        images: row.images,
      }).unwrap();

      const updated = [...updates];
      updated[index].status = { type: 'Success', message: 'Updated' };
      setUpdates(updated);
      toast.success(`Row ${index + 1} updated`);
    } catch (err) {
      const updated = [...updates];
      updated[index].status = {
        type: 'Failed',
        message: err?.data?.message || err.message,
      };
      setUpdates(updated);
      toast.error(`Row ${index + 1} failed: ${updated[index].status.message}`);
    }
  };

  const handleSubmitAll = async () => {
    const updatedRows = [...updates];

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.error) {
        updatedRows[i].status = { type: 'Skipped', message: row.error };
        continue;
      }

      try {
        await updateExpense({
          expenseId: row.expenseId,
          date: row.date,
          account: row.account,
          accountName: row.accountName,
          subAccount: row.subAccount,
          details: row.details,
          amount: row.amount,
          category: row.category,
          notes: row.notes,
          images: row.images,
        }).unwrap();

        updatedRows[i].status = { type: 'Success', message: 'Updated' };
      } catch (err) {
        updatedRows[i].status = {
          type: 'Failed',
          message: err?.data?.message || err.message,
        };
      }
    }

    setUpdates(updatedRows);
    toast.success('✅ All expense rows processed');
  };

  /*const formatDate = (value) => {
  const d = new Date(value);
  return isNaN(d) ? '' : d.toISOString().split('T')[0];
};*/

  return (
    <div className="container mt-4">
      <h2>💸 Bulk Expense Update with Image Upload</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {updates.length > 0 && (
        <>
          <h5>Preview & Edit: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Expense ID</th>
                <th>Account Name</th>
                <th>Date</th>
                <th>Account</th>
                <th>SubAccount</th>
                <th>Details</th>
                <th>Amount</th>
                <th>Category</th>
                <th>Notes</th>
                <th>Images</th>
                <th>Upload Image</th>
                <th>Submit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td><Form.Control value={row.expenseId} readOnly plaintext /></td>
                  <td><Form.Control value={row.accountName} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].accountName = e.target.value;
                    setUpdates(updated);
                  }} /></td> 
                  <td> <Form.Control
                                        type="date"
                                        value={row.date}
                                        onChange={(e) => {
                                          const updated = [...updates];
                                          updated[idx].date = e.target.value;
                                          setUpdates(updated);
                                        }}
                                      />
                                      </td>
                  <td><Form.Select value={row.account} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].account = e.target.value;
                    setUpdates(updated);
                  }}>
                    <option value="">Select</option>
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </Form.Select></td>
                  <td><Form.Control value={row.subAccount} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].subAccount = e.target.value;
                    setUpdates(updated);
                  }} /></td>
                  <td><Form.Control value={row.details} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].details = e.target.value;
                    setUpdates(updated);
                  }} /></td>
                  <td><Form.Control type="number" value={row.amount} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].amount = parseFloat(e.target.value);
                    setUpdates(updated);
                  }} /></td>
                  <td><Form.Select value={row.category} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].category = e.target.value;
                    setUpdates(updated);
                  }}>
                    <option value="">Select</option>
                    {['Consult', 'SalesCredit', 'SalesPayment', 'supplyPayment', 'Service', 'Lab', 'Electricity', 'Travel', 'Food', 'Office Supplies', 'Other', "Observation", "Admission", "Electricity", "Dressing", "Delivery", "Expense"].map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select></td>
                  <td><Form.Control value={row.notes} onChange={(e) => {
                    const updated = [...updates];
                    updated[idx].notes = e.target.value;
                    setUpdates(updated);
                  }} /></td>
                  
                  <td>
                    {row.images.map((img, i) => (
                        <img
                        key={i}
                        src={img}
                        alt="preview"
                        style={{ width: '20px', height: '20px', marginRight: '5px' }}
                      />
                    ))}
                  </td>
                  <td>
                    <Form.Control type="file" onChange={(e) => handleImageUpload(e, idx)} />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleRowSubmit(row, idx)}
                      disabled={loadingUpdate}
                    >
                      {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </td>
                  <td>
                    {row.status ? (
                      <span
                        className={
                          row.status.type === 'Success'
                            ? 'text-success'
                            : row.status.type === 'Skipped'
                            ? 'text-warning'
                            : 'text-danger'
                        }
                      >
                        {row.status.type} — {row.status.message}
                      </span>
                    ) : row.error ? (
                      <span className="text-danger">{row.error}</span>
                    ) : (
                      <span className="text-muted">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button
            variant="success"
            onClick={handleSubmitAll}
            disabled={loadingUpdate}
            className="mb-3"
          >
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : '🚀 Submit All Valid Rows'}
          </Button>
        </>
      )}

      {updates.some((row) => row.status) && (
        <div className="mt-4">
          <h5>📋 Submission Summary</h5>
          {updates.map((row, i) =>
            row.status ? (
              <Alert
                key={i}
                variant={
                  row.status.type === 'Success'
                    ? 'success'
                    : row.status.type === 'Skipped'
                    ? 'warning'
                    : 'danger'
                }
              >
                Row {i + 1}: {row.status.type} — {row.status.message}
              </Alert>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default BulkEditExpensesScreen;